import React, { useRef, useState, useEffect } from 'react';
import { Insight } from '../services/api';
import { InsightCard } from './InsightCard';
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

interface InsightsCarouselProps {
  insights: Insight[];
  onVote: (id: string, vote: 1 | -1) => void;
  onShowAll: () => void;
  loading?: boolean;
}

export const InsightsCarousel: React.FC<InsightsCarouselProps> = ({
  insights,
  onVote,
  onShowAll,
  loading = false
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  
  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (ref) {
        ref.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [insights]);
  
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  
  if (!loading && insights.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div 
            className="p-2 rounded-xl"
            style={{ 
              background: 'linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 80%, #000) 100%)'
            }}
          >
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">AI Insights</h2>
            <p className="text-xs text-gray-500">
              {loading ? 'Analyzing...' : `${insights.length} insight${insights.length !== 1 ? 's' : ''} for you`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Scroll buttons */}
          {insights.length > 2 && (
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className={`p-2 rounded-full transition-all duration-200 ${
                  canScrollLeft 
                    ? 'bg-white shadow-sm hover:shadow-md hover:scale-105 text-gray-600' 
                    : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className={`p-2 rounded-full transition-all duration-200 ${
                  canScrollRight 
                    ? 'bg-white shadow-sm hover:shadow-md hover:scale-105 text-gray-600' 
                    : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowAll}
            className="text-xs text-gray-600 hover:text-gray-900 gap-1 hover:bg-gray-100/80"
          >
            View All
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      
      {/* Content - overflow-visible allows hover effects to extend beyond container */}
      <div className="relative overflow-visible">
        {/* Loading skeleton */}
        {loading && (
          <div className="flex gap-4 overflow-hidden">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="min-w-[300px] max-w-[340px] h-[160px] rounded-2xl overflow-hidden"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="h-full w-full bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 shimmer" />
              </div>
            ))}
          </div>
        )}
        
        {/* Carousel */}
        {!loading && insights.length > 0 && (
          <>
            {/* Added py-2 for top/bottom padding to allow hover lift effect */}
            <div
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto overflow-y-visible scrollbar-hide py-2 -mx-1 px-1 snap-x snap-mandatory"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {insights.map((insight, index) => (
                <div
                  key={insight.id}
                  className="snap-start flex-shrink-0 fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <InsightCard
                    insight={insight}
                    onVote={onVote}
                    compact
                    truncate={false}
                  />
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Empty state */}
        {!loading && insights.length === 0 && (
          <div className="apple-card flex items-center justify-center py-8 px-4">
            <div className="text-center">
              <div 
                className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--theme-primary)10' }}
              >
                <Sparkles className="h-7 w-7" style={{ color: 'var(--theme-primary)' }} />
              </div>
              <p className="text-sm font-medium text-gray-700">No new insights</p>
              <p className="text-xs text-gray-500 mt-1">Check back tomorrow for fresh analysis</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsCarousel;
